// ════════════════════════════════════════════════════════════════════
// TERROR — MINERIUM ONE: THE DIVISION
// ────────────────────────────────────────────────────────────────────
// ⭐ THE BLUEPRINT LAW — caP'n, 2026-08-22, and this governs EVERY
// Minerium that will ever be built:
//
//   *"you get every single card in fable version but cool unique
//   sigil — then you copy that same blueprint but if you do the easter
//   egg version you get the better Mythical holo version see? ...kind
//   of a cool way the player knows if they should waste their time or
//   not getting the egg, maybe they just wanna chase one node in the
//   entire minerium that's fine."*
//
//   *"the mythics should be spread out.... so there should never for
//   absolutely no reason be a cool rare node like a freakin Sol King
//   and it not drop a mythic for doing the easter egg."*
//
// ── WHAT THAT MEANS, EXACTLY ────────────────────────────────────────
// ONE MAP. Not two. `NODE_LOOT` below says what a node gives you, and
// that is the whole system:
//
//   · Clear the node the ordinary way  → you get that bundle in FABLE.
//   · Clear it the EGG way             → you get that bundle in MYTHIC,
//                                        wearing `holo_vrown`.
//   · Do both (egg first counts as both) → you hold each card twice,
//                                        once plain, once foiled.
//
// EVERY node has an egg. Every egg pays the Mythic twin of what the
// node already pays. A player can read one row of this table and know
// precisely what a node is worth, decide it is not worth their time,
// and be right — which is the point caP'n made, and it is worth more
// than any amount of surprise.
//
// ── WHAT THIS REPLACED, AND WHY THE OLD SHAPE WAS WRONG ─────────────
// Twelve hours ago this file paid Fable by REGION and Mythic by
// SECRET, with two different maps, floors, top-ups and a legacy seal.
// It audited clean and it was still wrong, for one reason: a Sol King
// you beat perfectly could pay you nothing, because its wedge lived on
// a different track's map. caP'n's word for that was "atrocious," and
// he was right — the rare node not paying for the rare deed is the one
// thing a loot table must never do.
//
// The blueprint deletes all of it. No regions, no chambers, no floors,
// no top-ups, no legacy seal, no second ledger. One table, two states.
// That is also why there is no "you finished the story so here is
// everything you missed" grant any more: skipping a node means not
// having its cards, on purpose. Clearing all 55 IS the full Fable set.
// Egging all 55 IS the full Mythic set. The guarantee is the map.
//
// ── THE SHAPE OF THE MAP ────────────────────────────────────────────
// 55 fight-nodes, 177 collectible cards (Esclings excluded — they are
// field furniture and `progression.storySetCardIds` has always said
// so). Everything is derived from `cards.js`; the only hand-authored
// thing here is WHICH BUNDLE SITS ON WHICH NODE, which is the actual
// creative decision.
//
//   · An ordinary node pays a TRIPLE — three cards. A lineage's three
//     Escendants, its three Impera, a third of a Tome ilk, or three
//     Curios.
//   · A LORD'S CHAMBER pays TWO triples. Six of them, each a dead-end
//     spur behind its own door, and they should feel like it.
//   · THE EYELAND's six outer Sol Kings pay TWO U'HLTIS EACH — caP'n:
//     *"12 U'hltis, 6 spaces, 2 per node, boom."* All twelve U'hltis
//     live behind the Eye and nowhere else in the Minerium.
//   · THE PUPIL (ASS-50) pays NO CARDS AT ALL. It pays the Mythical
//     SHV Backdrop, Coin and Aura — see MINERIUM1_PUPIL. The deepest
//     door in the world hands over something no card can be.
//   · THE CRIMSON DAIS's four wardens pay one AFFINITY and two
//     PROPHECIES each — the twelve rarest card-shapes in the game, in
//     the four rooms you can only reach by Moon.
//   · THE CRIMSON JEWEL (AZS-56) pays the ANTDROID line — Sir H'osh
//     Vrown's own elite, and the only place in the Minerium it exists.
//
// Audited: 55 nodes, 177 cards, each exactly once. `auditMinerium1()`
// re-checks it in one call and should be run after any card edit.
// ════════════════════════════════════════════════════════════════════

import {
  ESCENDANTS, IMPERA, UHLTIS, TOMES, CURIOS, AFFINITIES, PROPHECIES,
} from "./cards";

// ── DERIVATION, NOT TYPING ───────────────────────────────────────────
// ⚠ LINEAGE IS THE ID STEM, NOT school+ilk. `h01_beb` / `h01_champion`
// / `h01_lord` share the stem `h01`; the Impera are `p_h01_*` and the
// U'hlti is `u_h01_lord`. Grouping by school+ilk instead is a bug that
// has been shipped here once already — H-01, H-09 and Xulan are ALL
// Humanoid/Space, so three "families" silently became one.
const LEVEL_SUFFIX = /_(beb|champion|lord)$/;
export const lineageOf = (cardId) => String(cardId || "").replace(LEVEL_SUFFIX, "");
export const LINEAGES = [...new Set(ESCENDANTS.map((e) => lineageOf(e.id)))];

// A lineage's three Escendants, Beb → Champion → Lord.
const esc = (stem) => ESCENDANTS
  .filter((e) => lineageOf(e.id) === stem)
  .sort((a, b) => (a.level || 0) - (b.level || 0))
  .map((e) => e.id);

// A lineage's three Impera, in card order.
const pri = (stem) => {
  const ids = new Set(esc(stem));
  return IMPERA.filter((p) => ids.has(p.owner)).map((p) => p.id);
};

// A lineage's U'hlti (its Lord's, always exactly one).
const uhl = (stem) => {
  const ids = new Set(esc(stem));
  return UHLTIS.filter((u) => ids.has(u.owner)).map((u) => u.id);
};

// One THIRD of a Tome ilk. Nine Tomes per ilk, three nodes per ilk, so
// `part` is 1, 2 or 3. Slicing rather than naming means a re-ordered or
// re-named Tome never orphans a node.
const tome = (ilk, part) => TOMES.filter((t) => t.ilk === ilk).map((t) => t.id).slice((part - 1) * 3, part * 3);

// One third of the Curios, same law.
const curio = (part) => CURIOS.map((c) => c.id).slice((part - 1) * 3, part * 3);

const byId = (arr, ids) => ids.map((id) => arr.find((c) => c.id === id)?.id).filter(Boolean);

// The whole collectible set — 177. Esclings excluded, deliberately.
export const MINERIUM1_SET = [
  ...ESCENDANTS.map((c) => c.id), ...IMPERA.map((c) => c.id), ...UHLTIS.map((c) => c.id),
  ...TOMES.map((c) => c.id), ...CURIOS.map((c) => c.id),
  ...AFFINITIES.map((c) => c.id), ...PROPHECIES.map((c) => c.id),
];

// ════════════════════════════════════════════════════════════════════
// NODE_LOOT — THE BLUEPRINT
// ════════════════════════════════════════════════════════════════════
// node id → { label, cards[] }. Clear it → these in Fable. Egg it →
// these in Mythic. There is no third case and there never will be.
//
// The `label` is the room's own name, because it is what the reward
// ribbon says and what the provenance sigil is FOR — a player who
// pulled the Radiant Shower out of the Upper Lid should be able to
// remember that in a year.
export const NODE_LOOT = {

  // ══ ZONE A · BONEYARD SOUTH ═══════════════════════════════════════
  // The first eleven fights, and the first two lineages you will ever
  // hold. H-01 are the ones who "choose words as their first resort,"
  // which is the whole south yard: an oath, a paper, and a gate that
  // disagrees. The CONTRACT tomes are here for the same reason, split
  // across the three nodes that are each about a promise somebody is
  // still standing on.
  "AAC-01": { label: "The Roadhead",        cards: tome("Contract", 1) },
  "AAC-02": { label: "The Kettle Steps",    cards: esc("h01") },
  "AAC-03": { label: "The Two-Spade Field", cards: pri("h01") },
  // Ossuar the Rib-Warden — a Lucky Dragon, and the Minerium's first
  // Sol King. His own line, off his own corpse-house.
  "AAS-04": { label: "The Ribcage",         cards: esc("lucky_dragon") },
  "AAC-05": { label: "The Low Cairn",       cards: pri("lucky_dragon") },
  // Bartleby Nix has sold this ground eleven times and kept every
  // receipt in beautiful order. Of course he pays Contract.
  "AAC-06": { label: "The Sold Ground",     cards: tome("Contract", 2) },
  "AAC-07": { label: "The Widow's Rest",    cards: tome("Contract", 3) },
  "AAC-08": { label: "The Rookery",         cards: esc("dg0") },
  "AAC-09": { label: "The Counting House",  cards: pri("dg0") },
  "AAC-15": { label: "The Long Gate",       cards: curio(1) },
  // ── LORD'S CHAMBER — two triples, and it should feel like a vault.
  "AAL-16": { label: "The Bone Chamber",    cards: [...esc("z4z"), ...tome("Ritual", 1)] },

  // ══ ZONE B · HARSH MARSH WEST ═════════════════════════════════════
  // "Two of you. One shadow between you." The Fox line, and the rites
  // that make one thing out of two.
  "ABC-10": { label: "The Wading Line",     cards: esc("shadow_fox") },
  "ABC-11": { label: "The Still Reach",     cards: pri("shadow_fox") },
  "ABC-12": { label: "The Drowned Choir",   cards: tome("Ritual", 2) },
  "ABL-13": { label: "The Still Chamber",   cards: [...pri("z4z"), ...tome("Ritual", 3)] },
  "ABS-14": { label: "The Green Tide",      cards: esc("spectral_tiger") },

  // ══ ZONE C · SHV'S MAUSOLEUM ══════════════════════════════════════
  // The longest zone and the oldest. ANCIENT SORCERY runs through its
  // middle three rooms; SHADOW GAMBLING runs through the three that
  // lead to J'ynxi's leftover, because that is her table and always was.
  "ACC-17": { label: "The West Approach",   cards: pri("spectral_tiger") },
  "ACC-18": { label: "The Quillon Steps",   cards: esc("inverted_serpent") },
  // Sov'rn records and does not interpret. The Serpent is inverted the
  // way a ledger entry is inverted.
  "ACS-19": { label: "The Tally Vault",     cards: pri("inverted_serpent") },
  "ACC-20": { label: "The Ash Gallery",     cards: tome("Ancient Sorcery", 1) },
  "ACC-21": { label: "The Sealed Wing",     cards: tome("Ancient Sorcery", 2) },
  "ACC-22": { label: "The Reliquary",       cards: tome("Ancient Sorcery", 3) },
  "ACC-23": { label: "The Coffin Shop",     cards: curio(2) },
  "ACC-25": { label: "The East Cloister",   cards: tome("Shadow Gambling", 1) },
  "ACL-26": { label: "The Black Chamber",   cards: [...esc("h09"), ...tome("Shadow Gambling", 2)] },
  "ACC-27": { label: "The North Stair",     cards: tome("Shadow Gambling", 3) },

  // ══ ZONE D · HARSH MARSH EAST ═════════════════════════════════════
  // One node, and it is a Chamber. The Waiting Chamber, and the last
  // three small things that have been waiting.
  "ADL-24": { label: "The Waiting Chamber", cards: [...pri("h09"), ...curio(3)] },

  // ══ ZONE E · DESERTED DESERT ══════════════════════════════════════
  // Xulan wander; the desert keeps what it buries. TREASURE, across the
  // market, the vein and the hour that stopped.
  "AEC-28": { label: "The First Dune",      cards: esc("xulan") },
  "AEC-29": { label: "The Glassworks",      cards: pri("xulan") },
  "AEC-30": { label: "The Sunk Market",     cards: tome("Treasure", 1) },
  "AEC-31": { label: "The Marrow Vein",     cards: tome("Treasure", 2) },
  "AES-32": { label: "The Stopped Hour",    cards: tome("Treasure", 3) },
  "AEL-33": { label: "The Chamber of Nowhere", cards: [...esc("premier_dragon"), ...tome("Nihil", 1)] },
  "AES-34": { label: "The Second Sun",      cards: esc("chaos_dragon") },

  // ══ ZONE F · BONEYARD NORTH ═══════════════════════════════════════
  // Rük's line of candles. OMNIA catalogues; the Blades of Omnis are
  // what this end of the world is for.
  "AFC-35": { label: "The Tallow Line",     cards: pri("chaos_dragon") },
  "AFC-36": { label: "The Skull Ledger",    cards: tome("Omnia", 1) },
  "AFC-37": { label: "The Herald's Field",  cards: tome("Omnia", 2) },
  "AFC-38": { label: "The Ashcroft Pyre",   cards: tome("Omnia", 3) },
  "AFS-39": { label: "The Long Winter",     cards: pri("premier_dragon") },

  // ══ ZONE G · SHV'S CRYSTAL MAUSOLEUM ══════════════════════════════
  // The only place in the world that was MADE rather than grown, so it
  // pays CONSTRUCTION — and its Chamber pays the Swords of Nullus, two
  // thirds of NIHIL in one room, which is the richest Tome drop in the
  // Minerium and the reason that door is worth the walk.
  "AGS-40": { label: "The Seventh Facet",   cards: tome("Construction", 1) },
  "AGS-41": { label: "The Cathedra",        cards: tome("Construction", 2) },
  "AGS-42": { label: "The Counted Light",   cards: tome("Construction", 3) },
  "AGL-43": { label: "The Crystal Chamber", cards: [...tome("Nihil", 2), ...tome("Nihil", 3)] },

  // ══ ZONE S · THE EYELAND ══════════════════════════════════════════
  // ⭐ caP'n: *"12 U'hltis, 6 spaces, 2 per node, boom."*
  // Every U'hlti in the game is behind the Eye and nowhere else. Six
  // Sol Kings, six doors, and the pairs are by kinship rather than by
  // Ilk alone — the two H-lines together, the two that hide together,
  // the Sword and the Shield together.
  "ASS-44": { label: "The Lower Lid",  cards: [...uhl("h01"), ...uhl("h09")] },
  "ASS-45": { label: "The Tear Duct",  cards: [...uhl("xulan"), ...uhl("shadow_fox")] },
  "ASS-46": { label: "The Outer Ring", cards: [...uhl("spectral_tiger"), ...uhl("inverted_serpent")] },
  // H'ehll's Sword and I'nfini's Shield, in the one room called White.
  "ASS-47": { label: "The White",      cards: [...uhl("z4z"), ...uhl("dg0")] },
  "ASS-48": { label: "The Upper Lid",  cards: [...uhl("antdroid"), ...uhl("lucky_dragon")] },
  "ASS-49": { label: "The Blind Spot", cards: [...uhl("premier_dragon"), ...uhl("chaos_dragon")] },

  // ── THE PUPIL pays no cards. See MINERIUM1_PUPIL below. It is listed
  //    here with an empty bundle rather than omitted, because a node
  //    missing from this table is indistinguishable from a node
  //    somebody forgot, and the audit has to be able to tell.
  "ASS-50": { label: "The Pupil", cards: [], cosmeticsOnly: true },

  // ══ ZONE Z · THE CRIMSON DAIS ═════════════════════════════════════
  // Reached only by the Moon, at fifteen Sol Shards. The four wardens
  // hold the twelve rarest card-shapes in the game: one Affinity and
  // two Prophecies each, matched to what the room is about.
  "AZS-52": { label: "The Hollow",        cards: byId(AFFINITIES, ["aff_evil"]).concat(byId(PROPHECIES, ["proph_ace_of_shades", "proph_bloody_hell"])) },
  "AZS-53": { label: "The Last Kiln",     cards: byId(AFFINITIES, ["aff_holy"]).concat(byId(PROPHECIES, ["proph_dragonforce", "proph_tablet_omnis"])) },
  "AZS-54": { label: "The Pulp",          cards: byId(AFFINITIES, ["aff_awoken"]).concat(byId(PROPHECIES, ["proph_space_race", "proph_time_to_pretend"])) },
  "AZS-55": { label: "The Brood Gallery", cards: byId(AFFINITIES, ["aff_jynxi"]).concat(byId(PROPHECIES, ["proph_phantom_planet", "proph_tablet_nullus"])) },

  // ⭐ THE CRIMSON JEWEL. The Antdroid line is Sir H'osh Vrown's own
  // elite — he is an Antdroid himself — and beating him is the only
  // way a soldier's colours have ever changed hands. Their U'hlti is
  // in the Upper Lid; the line itself is here.
  "AZS-56": { label: "The Crimson Jewel", cards: [...esc("antdroid"), ...pri("antdroid")] },
};

// ── THE PUPIL'S PRIZE ────────────────────────────────────────────────
// ⭐ caP'n: *"the middle pupil will give you a Mythical SHV Backdrop
// and Coin and Lux/Grim aura."*
//
// The one node in the Minerium that pays no card, and it is the
// deepest door in the world — reachable only by beating all six Sol
// Kings around the Eye, or by solving all four eggs on the Dais. So it
// pays the things a card cannot be: a room, a coin, and a light.
//
// Clearing it grants them; EGGING it grants them too and nothing more,
// because a backdrop has no foil — which is stated here rather than
// left implicit, so the next Minerium does not invent a "Mythic
// backdrop variant" to fill a symmetry that was never real.
export const MINERIUM1_PUPIL = {
  node: "ASS-50",
  backdrop: "shv_crimson",     // RoomBackdrop.js — mythic rarity
  coinSkin: "shv_griddle",     // coinSkins.js — mythic, Divine Crests
  aura: "shv_sear",            // CosmeticManifest.js aura slot
};

// ════════════════════════════════════════════════════════════════════
// THE API — what progression.js and worldData.js call
// ════════════════════════════════════════════════════════════════════

// What node `nodeId` pays, in the given track. `track` is "fable"
// (cleared the ordinary way) or "mythic" (cleared the egg way). The
// LIST IS THE SAME either way — that is the blueprint — and the track
// only decides whether the cards arrive foiled.
export const lootForNode = (nodeId) => NODE_LOOT[nodeId] || null;
export const cardsForNode = (nodeId) => NODE_LOOT[nodeId]?.cards || [];
export const labelForNode = (nodeId) => NODE_LOOT[nodeId]?.label || nodeId;

// The foil every Mythic card from this Minerium wears. ONE location,
// ONE holo — see HoloFoil.js `holo_vrown`, "The Griddle." A future
// Minerium mints its own tier; this one is never reissued.
export const MINERIUM1_MYTHIC_HOLO_TIER = "holo_vrown";

// The provenance marks. SourceSigil.js — the Griddle seal, and the
// same seal wearing the Griddle's own sear.
export const MINERIUM1_SOURCE = { fable: "minerium1_fable", mythic: "minerium1_mythic" };

// ── THE AUDIT ────────────────────────────────────────────────────────
// The thing that goes wrong in a loot table is a card that belongs to
// NO node — invisible in play, invisible in review, and only ever
// found by counting. Exported rather than written as a test so the
// Debugger can ask it live.
//
//   import { auditMinerium1 } from "./minerium1Rewards";
//   console.log(auditMinerium1());
//
// `ok` is true only when the 55 nodes cover all 177 cards exactly once.
export function auditMinerium1(nodeIds) {
  const seen = new Map();
  for (const w of Object.values(NODE_LOOT)) for (const id of w.cards) seen.set(id, (seen.get(id) || 0) + 1);
  const set = new Set(MINERIUM1_SET);
  const out = {
    setSize: MINERIUM1_SET.length,
    nodes: Object.keys(NODE_LOOT).length,
    covered: seen.size,
    missing: MINERIUM1_SET.filter((id) => !seen.has(id)),
    duplicated: [...seen.entries()].filter(([, n]) => n > 1).map(([id]) => id),
    stranger: [...seen.keys()].filter((id) => !set.has(id)),
    empty: Object.entries(NODE_LOOT).filter(([, w]) => !w.cards.length && !w.cosmeticsOnly).map(([n]) => n),
  };
  // If the caller hands us the world's real node list, also report any
  // node the map forgot — the failure mode a card-count audit cannot
  // see, because a forgotten node's cards were simply never assigned.
  if (Array.isArray(nodeIds)) {
    out.unmappedNodes = nodeIds.filter((id) => !NODE_LOOT[id]);
    out.phantomNodes = Object.keys(NODE_LOOT).filter((id) => !nodeIds.includes(id));
  }
  out.ok = !out.missing.length && !out.duplicated.length && !out.stranger.length && !out.empty.length
    && !(out.unmappedNodes || []).length && !(out.phantomNodes || []).length;
  return out;
}

const minerium1Rewards = {
  NODE_LOOT, MINERIUM1_SET, MINERIUM1_PUPIL,
  lootForNode, cardsForNode, labelForNode,
  MINERIUM1_MYTHIC_HOLO_TIER, MINERIUM1_SOURCE, auditMinerium1,
};
export default minerium1Rewards;
